// Cruise Command · Create Cruise Workflow · Field Library
// -------------------------------------------------------------------
// Restored from the 2026-03-06 PO-approved Create Cruise workflow
// (deleted 2026-07-15 during Foundation Freeze) and MWO-003-modernized.
//
// Every field on a cruise falls in one of these groups. Fields that
// were `finance_items` writers in the deleted implementation are
// **not** present here — those responsibilities were retired by
// MWO-003 · Canonical Voyage Financials. What remains is
// booking-metadata: information that belongs to the cruise itself,
// not to any ledger.
//
// ADR-CRUISE-001 · every field is additive on the voyage document.
// New fields land here as an additional entry in FIELD_DEFS and pick
// up UI + payload plumbing automatically.

export const FIELD_DEFS = [
  // Voyage core
  { key: "cruise_line",        label: "Cruise Line",        type: "text",   group: "voyage",  required: true  },
  { key: "ship",               label: "Ship",               type: "text",   group: "voyage",  required: true  },
  { key: "sailing_date",       label: "Embark Date",        type: "date",   group: "voyage",  required: true  },
  { key: "return_date",        label: "Disembark Date",     type: "date",   group: "voyage",  required: true  },
  { key: "embark_port",        label: "Embark Port",        type: "text",   group: "voyage" },
  { key: "name",               label: "Cruise Nickname",    type: "text",   group: "voyage" },

  // References
  { key: "booking_number",     label: "Booking Number",     type: "text",   group: "reference" },
  { key: "reservation_number", label: "Reservation Number", type: "text",   group: "reference" },
  { key: "reservation_status", label: "Reservation Status", type: "select", group: "reference",
    options: ["confirmed", "pending", "waitlisted", "cancelled"] },

  // Cabin
  { key: "cabin_status",       label: "Cabin Status",       type: "select", group: "cabin",
    options: ["assigned", "guarantee", "tbd"] },
  { key: "cabin_category",     label: "Cabin Category",     type: "text",   group: "cabin" },
  { key: "deck",               label: "Deck",               type: "text",   group: "cabin" },
  { key: "cabin_number",       label: "Cabin Number",       type: "text",   group: "cabin" },

  // Travelers
  { key: "travelers",          label: "Travelers",          type: "list",   group: "travelers" },

  // Booking metadata (money — booking attributes, NOT accounting)
  // Per PO directive 2026-02-18: MWO-003 remains authoritative. These
  // fields describe the booking, not ledger entries.
  { key: "booking_total",      label: "Booking Total",      type: "money",  group: "money" },
  { key: "currency",           label: "Currency",           type: "text",   group: "money", placeholder: "USD" },
  { key: "deposit_amount",     label: "Deposit Amount",     type: "money",  group: "money" },
  { key: "deposit_paid_date",  label: "Deposit Paid Date",  type: "date",   group: "money" },
  { key: "final_payment_date", label: "Final Payment Due",  type: "date",   group: "money" },
  { key: "onboard_credit",     label: "Onboard Credit",     type: "money",  group: "money" },

  // Extras
  { key: "dining_time",        label: "Dining Time",        type: "text",   group: "extras" },
  { key: "loyalty_number",     label: "Loyalty Number",     type: "text",   group: "extras" },
  { key: "promotions",         label: "Promotions",         type: "list",   group: "extras" },
  { key: "agent_name",         label: "Travel Agent Name",  type: "text",   group: "extras" },
  { key: "agent_agency",       label: "Agency",             type: "text",   group: "extras" },
];

export const GROUPS = [
  { key: "voyage",     label: "Cruise",       defaultOpen: true  },
  { key: "reference",  label: "References",   defaultOpen: true  },
  { key: "cabin",      label: "Cabin",        defaultOpen: true  },
  { key: "travelers", label: "Travelers",   defaultOpen: true  },
  { key: "money",      label: "Booking Costs", defaultOpen: false },
  { key: "extras",     label: "Extras",       defaultOpen: false },
];

/**
 * Split the extracted / edited field bag into:
 *   - `voyage_create`: the exact shape accepted by POST /api/voyages
 *   - `voyage_patch`:  the exact shape accepted by PATCH /api/cruises/{id}
 *
 * MWO-003 compliance: no finance_items are ever created from this
 * payload; booking-money fields land as additive properties on the
 * voyage document itself.
 */
export function splitPayload({ fields, lineId, shipId, embarkPortId }) {
  const trimStr = (v) => (typeof v === "string" ? v.trim() : v);
  const num = (v) => (v === "" || v == null ? null : Number(v));

  const create = {
    name: trimStr(fields.name) || _defaultName(fields),
    cruise_line_id: lineId || null,
    ship_id: shipId || null,
    // RC2 MXR (2026-07-22) · P0 fix — persist the traveler's free-text
    // cruise line + ship values so the Cruises list, Edit Voyage drawer,
    // Dashboard hero and Sidebar never render "Ship TBD" after a
    // successful wizard-driven create.
    cruise_line_name: trimStr(fields.cruise_line) || null,
    ship_name: trimStr(fields.ship) || null,
    embark_port_id: embarkPortId || null,
    embark_date: fields.sailing_date || null,
    disembark_date: fields.return_date || null,
  };

  // Patch payload · additive booking metadata. Only include keys that
  // have real values so we never inadvertently null-out an existing
  // field on the voyage doc.
  //
  // PV-006 / PV-007 · Field names below map to the FROZEN canonical
  // Voyage schema (see routes_voyages.py:VoyagePatchRequest). Never
  // send private wizard names like `booking_total`, `final_payment_date`,
  // or `currency` — Pydantic will silently drop them and the resulting
  // cruise will show blanks in the Canonical Financials Panel.
  const patchRaw = {
    // References
    booking_number: trimStr(fields.booking_number),
    reservation_number: trimStr(fields.reservation_number),
    reservation_status: fields.reservation_status,
    // Cabin
    cabin_status: fields.cabin_status,
    cabin_category: trimStr(fields.cabin_category),
    deck: trimStr(fields.deck),
    cabin_number: trimStr(fields.cabin_number),
    // Money — CANONICAL names (MWO-003 · VoyagePatchRequest)
    cruise_fare: num(fields.booking_total),
    fare_currency: trimStr(fields.currency),
    deposit_amount: num(fields.deposit_amount),
    // deposit_paid_date is a booking-metadata timestamp — not a canonical
    // financial field. Keep on voyage top level for the Voyage Profile
    // drawer to display.
    deposit_paid_date: fields.deposit_paid_date,
    final_payment_due: fields.final_payment_date,
    onboard_credit: num(fields.onboard_credit),
    // Extras
    dining_time: trimStr(fields.dining_time),
    loyalty_number: trimStr(fields.loyalty_number),
    promotions: Array.isArray(fields.promotions) && fields.promotions.length ? fields.promotions : undefined,
    // RC2 · Canonical Travel Agent link (LAW-001). `travel_agent_id` is
    // the source of truth; the legacy inline blob is only persisted when
    // the wizard didn't offer a picker (backward-compat for older flows).
    travel_agent_id: trimStr(fields.travel_agent_id) || undefined,
    travel_agent: (!fields.travel_agent_id && (fields.agent_name || fields.agent_agency))
      ? { name: trimStr(fields.agent_name), agency: trimStr(fields.agent_agency) }
      : undefined,
    travelers: Array.isArray(fields.travelers) && fields.travelers.length ? fields.travelers : undefined,
    traveler_ids: Array.isArray(fields.traveler_ids) && fields.traveler_ids.length ? fields.traveler_ids : undefined,
  };
  const patch = {};
  for (const [k, v] of Object.entries(patchRaw)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v === "") continue;
    patch[k] = v;
  }

  return { voyage_create: create, voyage_patch: patch };
}

function _defaultName(fields) {
  const line = (fields.cruise_line || "").trim();
  const ship = (fields.ship || "").trim();
  const sail = fields.sailing_date;
  if (ship && sail) {
    try {
      const m = /^(\d{4})-(\d{2})/.exec(sail);
      if (m) {
        const monthYear = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, 1)
          .toLocaleDateString(undefined, { month: "short", year: "numeric" });
        return `${ship} · ${monthYear}`;
      }
    } catch { /* ignore */ }
  }
  if (ship && line) return `${line} · ${ship}`;
  if (ship) return ship;
  if (line) return `${line} cruise`;
  return "New cruise";
}

/**
 * Validate step-1 required fields (cruise_line + ship + sailing_date
 * + return_date per PO directive · LAW-001 as of 2026-08-07). Both
 * cruise_line and ship are stored as canonical ids; the picker
 * simultaneously populates the display-name text on `fields` for
 * downstream persistence, but the picker guarantees non-empty
 * `cruise_line` / `ship` whenever an id is present.
 *
 * Returns null when valid, else a user-facing error string.
 */
export function validateRequired(fields) {
  const missing = [];
  if (!(fields.cruise_line || "").trim()) missing.push("Cruise line");
  if (!(fields.ship || "").trim()) missing.push("Ship");
  if (!fields.sailing_date) missing.push("Embark date");
  if (!fields.return_date) missing.push("Disembark date");
  if (missing.length) return `Please provide: ${missing.join(", ")}.`;
  return null;
}
